import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, Mail, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTechnicians() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (error) {
      toast.error('Error al cargar técnicos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.post(`${API}/technicians`, formData);
      toast.success('Técnico creado correctamente');
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '' });
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear técnico');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este técnico?')) return;
    
    try {
      await axios.delete(`${API}/technicians/${id}`);
      toast.success('Técnico eliminado');
      fetchTechnicians();
    } catch (error) {
      toast.error('Error al eliminar técnico');
    }
  };

  return (
    <Layout title="Gestión de Técnicos">
      <div className="space-y-6 animate-fade-in" data-testid="admin-technicians">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Gestiona los técnicos de tu equipo</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="add-technician-btn">
                <Plus className="w-4 h-4" />
                Nuevo Técnico
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Nuevo Técnico</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre Completo *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Juan García"
                    data-testid="technician-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="tecnico@navalec.com"
                    data-testid="technician-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Contraseña *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Mínimo 6 caracteres"
                    data-testid="technician-password-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="save-technician-btn">
                    Crear Técnico
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Technicians Grid */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : technicians.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay técnicos registrados</p>
              <p className="text-sm text-slate-500 mt-1">Crea el primer técnico para que pueda registrar trabajos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {technicians.map((tech) => (
              <Card key={tech.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400 font-bold text-lg">
                          {tech.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-rajdhani text-lg font-semibold text-white">{tech.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-slate-400">
                          <Mail className="w-3 h-3" />
                          <span>{tech.email}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(tech.id)}
                      className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10"
                      data-testid={`delete-technician-${tech.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <span className="px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                      Técnico Activo
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
